import type { ProfileType } from '../types';
import type { SeverityLevel } from '../context/ConfigurationContext';

export interface LayoutConfig {
  id: string;
  name: string;
  description: string;
  shortDescription: string; // 3-5 word differentiator for Good-Better-Best
  imagePath: string;
  contactCount: number;
  has911: boolean;
  hasAssistance: boolean;
  hasDialer: boolean;
}

export interface LayoutOption {
  layout: LayoutConfig;
  isRecommended: boolean;
}

// 7 launch layouts — matches Figma source of truth (AidFone — MASTER, file CBiMySuC0CKBiES2SPF22a)
// IDs match Android LayoutTypes.kt template IDs (one-to-one).
// Layouts not in Figma launch set were removed; legacy mb_* renamed to cb_*.
export const ALL_LAYOUTS: Record<string, LayoutConfig> = {
  // Physical A — numpad-driven (Figma PA1, PA2)
  pa_1: {
    id: 'pa_1',
    name: 'Numpad + Photos',
    description: 'Numpad with 911, Photos, Contact, and ASK',
    shortDescription: 'Dial, talk & photos',
    imagePath: '/images/layouts/figma/pa_1.png',
    contactCount: 1,
    has911: true,
    hasAssistance: true,
    hasDialer: true,
  },
  pa_2: {
    id: 'pa_2',
    name: 'Numpad + ASK',
    description: 'Numpad with 911, Contact, and ASK',
    shortDescription: 'Dial & talk',
    imagePath: '/images/layouts/figma/pa_2.png',
    contactCount: 1,
    has911: true,
    hasAssistance: true,
    hasDialer: true,
  },

  // Physical B — 2-column contact grid (Figma PB2)
  pb_2: {
    id: 'pb_2',
    name: '8 Contacts Grid',
    description: '911 + Photos, 8 contacts in 2-column grid, ASK + END',
    shortDescription: 'Many contacts',
    imagePath: '/images/layouts/figma/pb_2.png',
    contactCount: 8,
    has911: true,
    hasAssistance: true,
    hasDialer: false,
  },

  // Physical C — single column (Figma PC2)
  pc_2: {
    id: 'pc_2',
    name: '5 Large Buttons',
    description: '911 + Photos, 5 large contacts in single column, ASK + END',
    shortDescription: 'Larger buttons',
    imagePath: '/images/layouts/figma/pc_2.png',
    contactCount: 5,
    has911: true,
    hasAssistance: true,
    hasDialer: false,
  },

  // Confusion B — photo cards only (Figma CB1, formerly mb_1)
  cb_1: {
    id: 'cb_1',
    name: '3 Photo Contacts',
    description: '3 large photo contacts for easy recognition',
    shortDescription: 'Photo cards',
    imagePath: '/images/layouts/figma/cb_1.png',
    contactCount: 3,
    has911: false,
    hasAssistance: false,
    hasDialer: false,
  },

  // Visual — photo + giant ASK (Figma VI1, VI2)
  vi_1: {
    id: 'vi_1',
    name: '1 Photo + Giant ASK',
    description: '1 photo contact with giant voice button',
    shortDescription: 'Voice focused',
    imagePath: '/images/layouts/figma/vi_1.png',
    contactCount: 1,
    has911: false,
    hasAssistance: true,
    hasDialer: false,
  },
  vi_2: {
    id: 'vi_2',
    name: '2 Photos + ASK',
    description: '2 photo contacts with voice button',
    shortDescription: 'Two contacts',
    imagePath: '/images/layouts/figma/vi_2.png',
    contactCount: 2,
    has911: false,
    hasAssistance: true,
    hasDialer: false,
  },
};

// Layout options for each profile + severity combination.
// Scoped to the 7 Figma launch layouts; severities now share fewer options.
type LayoutMapping = {
  [profile in ProfileType]: {
    [severity in SeverityLevel]: {
      layoutIds: string[];
      recommendedId: string;
    };
  };
};

const LAYOUT_MAPPING: LayoutMapping = {
  cognitive: {
    severe: {
      layoutIds: ['cb_1'],
      recommendedId: 'cb_1',
    },
    moderate: {
      layoutIds: ['cb_1', 'pc_2'],
      recommendedId: 'cb_1',
    },
    mild: {
      layoutIds: ['pc_2', 'pb_2'],
      recommendedId: 'pc_2',
    },
  },
  physical: {
    severe: {
      layoutIds: ['cb_1'],
      recommendedId: 'cb_1',
    },
    moderate: {
      layoutIds: ['pb_2', 'pc_2', 'cb_1'],
      recommendedId: 'pc_2',
    },
    mild: {
      layoutIds: ['pa_1', 'pa_2', 'pb_2', 'pc_2'],
      recommendedId: 'pa_1',
    },
  },
  visual: {
    severe: {
      layoutIds: ['vi_1'],
      recommendedId: 'vi_1',
    },
    moderate: {
      layoutIds: ['vi_1', 'vi_2'],
      recommendedId: 'vi_1',
    },
    mild: {
      layoutIds: ['vi_1', 'vi_2'],
      recommendedId: 'vi_2',
    },
  },
  custom: {
    severe: {
      layoutIds: ['cb_1'],
      recommendedId: 'cb_1',
    },
    moderate: {
      layoutIds: ['cb_1', 'pc_2'],
      recommendedId: 'cb_1',
    },
    mild: {
      layoutIds: ['pc_2', 'pb_2'],
      recommendedId: 'pc_2',
    },
  },
};

// Get layout options for a profile + severity (returns 1-4 layouts with recommended flag)
export function getLayoutOptions(
  profile: ProfileType,
  severity: SeverityLevel | null
): LayoutOption[] {
  const sev = severity || 'moderate';
  const mapping = LAYOUT_MAPPING[profile]?.[sev];

  if (!mapping || mapping.layoutIds.length === 0) {
    return [];
  }

  return mapping.layoutIds
    .map(id => ALL_LAYOUTS[id])
    .filter(Boolean)
    .map(layout => ({
      layout,
      isRecommended: layout.id === mapping.recommendedId,
    }));
}

// Get the recommended layout ID for a profile + severity
export function getRecommendedLayoutId(
  profile: ProfileType,
  severity: SeverityLevel | null
): string | null {
  const sev = severity || 'moderate';
  const mapping = LAYOUT_MAPPING[profile]?.[sev];
  return mapping?.recommendedId || null;
}

// Get a layout by its ID
export function getLayoutById(layoutId: string): LayoutConfig | undefined {
  return ALL_LAYOUTS[layoutId];
}

/**
 * Returns the locale-appropriate layout preview image path.
 *
 * Convention: FR-localized PNGs live at /images/layouts/figma/fr/{same-name}.png
 * (same dimensions and crop as EN — produced by the designer from the Figma
 * "Layout Screens - FR - For Mokcups" section). For any non-FR locale we fall
 * back to the EN imagePath, so adding a third locale later is one line below.
 */
export function getLayoutImage(layout: LayoutConfig, locale?: string): string {
  if (locale?.startsWith('fr')) {
    return layout.imagePath.replace('/images/layouts/figma/', '/images/layouts/figma/fr/');
  }
  return layout.imagePath;
}

// Check if visual profile is available
export function isVisualProfileAvailable(): boolean {
  return true;
}

// Legacy function for backwards compatibility
export function getLayoutsForProfile(profile: ProfileType): LayoutConfig[] {
  const allLayoutIds = new Set<string>();

  (['severe', 'moderate', 'mild'] as SeverityLevel[]).forEach(severity => {
    const mapping = LAYOUT_MAPPING[profile]?.[severity];
    if (mapping) {
      mapping.layoutIds.forEach(id => allLayoutIds.add(id));
    }
  });

  return Array.from(allLayoutIds)
    .map(id => ALL_LAYOUTS[id])
    .filter(Boolean);
}
